import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Spin, Typography } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import AuthShell from "components/auth/AuthShell";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";

const { Paragraph, Title } = Typography;
const SESSION_COMPLETION_TIMEOUT_MS = 8000;

const completeSessionWithTimeout = (sessionPromise) =>
  Promise.race([
    sessionPromise,
    new Promise((resolve) => {
      window.setTimeout(() => resolve({ success: true, timedOut: true }), SESSION_COMPLETION_TIMEOUT_MS);
    }),
  ]);

const normalizeDateParam = (value) => {
  if (!value) return undefined;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? value : undefined;
};

export default function AuthLinkedInCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithSession } = useAuth();
  const hasStartedCompletionRef = useRef(false);
  const isMountedRef = useRef(true);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [error, setError] = useState("");

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const completeSignIn = async () => {
      if (hasStartedCompletionRef.current) return;
      hasStartedCompletionRef.current = true;

      const status = params.get("status");
      const callbackError = params.get("error");
      const token = params.get("token") || params.get("access_token");
      const messageParam = params.get("message");
      const identityToken = params.get("identity_token");
      const providerUserId = params.get("provider_user_id");
      const userInfoParam = params.get("user_info");

      if (callbackError || status === "error") {
        throw new Error(callbackError || "LinkedIn sign-in failed.");
      }
      if (status === "link_required" || status === "requires_password") {
        let userInfo = {};
        if (userInfoParam) {
          try {
            userInfo = JSON.parse(userInfoParam);
          } catch (parseError) {
            console.warn("Unable to parse LinkedIn linking profile:", parseError);
          }
        }
        navigate(paths.socialSetup || paths.appleSetup, {
          replace: true,
          state: {
            mode: "link",
            provider: "linkedin",
            authPayload: {
              identityToken,
              idToken: identityToken,
              providerUserId,
              linkedinUserId: providerUserId,
            },
            providerUserId,
            userInfo,
            message:
              messageParam ||
              "An account already exists with this email. Confirm linking to continue with LinkedIn.",
            requiresPassword: status === "requires_password",
            requiresLinkApproval: true,
          },
        });
        return;
      }
      if (status && status !== "success") {
        throw new Error("LinkedIn sign-in did not complete.");
      }
      if (!token) {
        throw new Error("LinkedIn did not return a session token.");
      }

      const result = await completeSessionWithTimeout(
        loginWithSession({
          accessToken: token,
          refreshToken: params.get("refresh_token") || undefined,
          accessTokenExpiresAt: normalizeDateParam(params.get("access_token_expires_at")),
          refreshTokenExpiresAt: normalizeDateParam(params.get("refresh_token_expires_at")),
        }),
      );

      if (!result?.success) {
        throw new Error(result?.error || "LinkedIn sign-in failed.");
      }

      if (isMountedRef.current) {
        navigate(params.get("next") || paths.pmDashboard, { replace: true });
      }
    };

    completeSignIn().catch((nextError) => {
      if (isMountedRef.current) {
        setError(nextError?.message || "LinkedIn sign-in failed.");
      }
    });
  }, [loginWithSession, navigate, params]);

  return (
    <AuthShell>
      <div style={{ maxWidth: 460, margin: "0 auto", textAlign: "center" }}>
        <Title level={2}>Completing LinkedIn sign in</Title>
        <Paragraph>We are finishing your secure LinkedIn session.</Paragraph>
        {error ? (
          <Alert type="error" showIcon message="LinkedIn sign-in failed" description={error} />
        ) : (
          <Spin size="large" />
        )}
      </div>
    </AuthShell>
  );
}
